import { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress
} from '@mui/material';
import { PageContext, ThemeContext } from '../Context';
import GetUserField from '../tool/GetUserField';
import CguContent from '../tool/CguContent';

function CGUPage() {
  const { setPage } = useContext(PageContext);
  const { theme } = useContext(ThemeContext);
  const [cguLang, setCguLang] = useState('fr');
  const [userLanguage, setUserLanguage] = useState('fr');
  const [cguEntries, setCguEntries] = useState([]);
  const [guardiaOn, setGuardiaOn] = useState(false);
  const [isCguLoading, setIsCguLoading] = useState(true);

  const isDark = theme === 'dark';

  useEffect(() => {
    if (userLanguage === 'fr' || userLanguage === 'nl') {
      setCguLang(userLanguage);
    }
  }, [userLanguage]);

  useEffect(() => {
    let mounted = true;

    const loadCguData = async () => {
      setIsCguLoading(true);
      try {
        const [cguResponse, guardiaResponse] = await Promise.all([
          fetch('/api/cgu'),
          fetch('/api/guardiasettings/public-status')
        ]);

        if (mounted && cguResponse.ok) {
          const cguData = await cguResponse.json();
          setCguEntries(Array.isArray(cguData) ? cguData : []);
        }

        if (mounted && guardiaResponse.ok) {
          const guardiaData = await guardiaResponse.json();
          setGuardiaOn(Boolean(guardiaData?.guardiaOn));
        }
      } catch (error) {
        console.error('Erreur lors du chargement des CGU:', error);
      } finally {
        if (mounted) {
          setIsCguLoading(false);
        }
      }
    };

    loadCguData();
    return () => {
      mounted = false;
    };
  }, []);

  const getCguContentByVersionAndLang = (version, lang) => {
    const entry = cguEntries.find((item) => item.version === version && item.lang === lang);
    return entry?.content || '';
  };

  const handleBack = () => {
    const previousPage = sessionStorage.getItem('cguPreviousPage');
    if (previousPage && previousPage !== 'CGU') {
      setPage(previousPage);
    } else {
      setPage('Edition');
    }
  };

  const currentBlueNoteContent = getCguContentByVersionAndLang('bluenote', cguLang);
  const currentGuardiaContent = guardiaOn ? getCguContentByVersionAndLang('guardia', cguLang) : '';
  const cguTitle = cguLang === 'fr'
    ? "Conditions d'utilisation et mentions légales"
    : 'Gebruiksvoorwaarden en wettelijke vermeldingen';

  return (
    <Box sx={{ p: 2 }}>
      <GetUserField fieldName="language" onLoad={setUserLanguage} />

      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 2,
          backgroundColor: isDark ? 'rgba(35, 48, 68, 0.9)' : 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(10px)',
          boxShadow: isDark ? '0 6px 24px rgba(0, 0, 0, 0.45)' : '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={handleBack}>
            Retour
          </Button>
          <Button variant="contained" onClick={() => setCguLang(cguLang === 'fr' ? 'nl' : 'fr')}>
            {cguLang === 'fr' ? 'Nederlands' : 'Français'}
          </Button>
        </Box>

        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
          {cguTitle}
        </Typography>

        {isCguLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ maxHeight: 'calc(100vh - 240px)', overflow: 'auto', pr: 1 }}>
            {currentBlueNoteContent ? (
              <CguContent content={currentBlueNoteContent} />
            ) : (
              <Typography variant="body2">
                {cguLang === 'fr' ? 'Aucun contenu disponible.' : 'Geen inhoud beschikbaar.'}
              </Typography>
            )}
            {guardiaOn && currentGuardiaContent && (
              <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography
                  variant="overline"
                  sx={{
                    display: 'block',
                    mb: 1,
                    fontWeight: 700,
                    letterSpacing: 1,
                    color: 'text.secondary',
                  }}
                >
                  {cguLang === 'fr'
                    ? 'Module additionnel GuardIA'
                    : 'Aanvullende module GuardIA'}
                </Typography>
                <CguContent content={currentGuardiaContent} />
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default CGUPage;
